import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { BranchDto } from '@repo/shared-types';
import { useBranches } from './useBranches';
import { useSubscription } from './useSubscription';
import { useAuthStore } from '../stores/authStore';
import { UNASSIGNED_BRANCH, activeBranchIds, useActiveBranchId, useBranchStore } from '../stores/branchStore';

export type BranchScopeState = 'all' | 'branch' | 'unassigned';

export interface BranchScope {
  orgId: string | null;
  branches: BranchDto[];
  unassignedSlotCount: number;
  branchId: string | null;
  state: BranchScopeState;
  label: string;
  select: (branchId: string | null) => void;
  visible: boolean;
}

/**
 * What every page that filters by branch should actually use instead of the
 * raw store value.
 *
 * THI-172: a non-Center org renders branchless — data intact, filtering off —
 * even when a branchId was selected and persisted BEFORE a downgrade and the
 * branch itself still exists. That case slips past `useBranchScope`'s
 * `reconcile` effect below, which only resets an id that no longer appears in
 * the active branch list — a still-live branch never trips it. Without this
 * wrapper, every page reading `useActiveBranchId()` directly (TimetablePage,
 * AttendanceMonthPage/AttendanceGrid, PayrollPage/TeacherDetailPanel,
 * WorkbenchPage/WeekStrip, StudentsPage) would keep silently narrowing a
 * dormant org's data with the switcher nowhere in sight to explain why —
 * indistinguishable from data loss. The reset is forced only once the plan is
 * CONFIRMED not to allow branches (`=== false`); while the subscription query
 * is still in flight this reports whatever was already selected, so a Center
 * org sees no regression and nothing flashes to "Tất cả" before its own data
 * has loaded. The switcher's `visible` predicate deliberately shares this
 * pending behaviour (`!== false`) — chrome and filtering may be wrong
 * TOGETHER for one round trip, but they must never disagree.
 */
export function useEffectiveBranchId(): string | undefined {
  const raw = useActiveBranchId();
  const { data: subscription } = useSubscription();
  return subscription?.plan.allowsBranches === false ? undefined : raw;
}

/**
 * Everything both switcher surfaces need, and the ONE place THI-137's two
 * effects live.
 *
 * `syncOrg` and `reconcile` moved here out of `BranchPicker` when that component
 * was replaced — they are load-bearing (an org switch must not carry a branch id
 * across a tenant boundary; a dead id must reset silently), and putting them in a
 * hook is what makes it impossible to drop one by deleting a component. Both are
 * idempotent, which is why it is fine that `BranchScopeRow` and
 * `BranchScopeStrip` are both mounted at once (one is CSS-hidden per breakpoint,
 * exactly as the two BranchPickers were).
 *
 * The store itself is hydrated at CREATION (THI-137 F4) — `useActiveBranchId`
 * already reports the right branch on the first render, so nothing here is
 * allowed to become the thing that hydrates it.
 */
export function useBranchScope(): BranchScope {
  const { t } = useTranslation('common');
  const { data } = useBranches();
  const branches = useMemo(() => data?.branches ?? [], [data]);
  const unassignedSlotCount = data?.unassignedSlotCount ?? 0;
  const orgId = useAuthStore((s) => s.organization?.id) ?? null;
  // Routes through the dormant-safe wrapper above, so the switcher's own state
  // (label, highlighted row) reports "Tất cả" on a dormant org exactly like
  // every other page does — "behave as scope=all EVERYWHERE" (THI-172).
  const branchId = useEffectiveBranchId() ?? null;
  const { data: subscription } = useSubscription();
  const select = useBranchStore((s) => s.select);
  const syncOrg = useBranchStore((s) => s.syncOrg);
  const reconcile = useBranchStore((s) => s.reconcile);

  useEffect(() => { if (orgId) syncOrg(orgId); }, [orgId, syncOrg]);
  useEffect(() => {
    if (!orgId || !data) return;
    // 'none' is a live scope only while there is something in it AND an active
    // branch remains to file it under (see `activeBranchIds` — final review
    // F2: without the branch-count clause, the last branch getting archived
    // left 'none' listed forever, since `unassignedSlotCount` alone doesn't
    // drop to 0 just because there is nowhere left to assign into).
    reconcile(orgId, activeBranchIds(branches, unassignedSlotCount));
  }, [orgId, data, branches, unassignedSlotCount, reconcile]);

  const state: BranchScopeState =
    branchId === null ? 'all' : branchId === UNASSIGNED_BRANCH ? 'unassigned' : 'branch';

  const label =
    state === 'all'
      ? t('branch.all')
      : state === 'unassigned'
        ? t('branch.unassigned')
        // A branch that vanished mid-render (archived in another tab) falls back
        // to "Tất cả" for one paint; `reconcile` above corrects the state next.
        : (branches.find((b) => b.id === branchId)?.name ?? t('branch.all'));

  return {
    orgId,
    branches,
    unassignedSlotCount,
    branchId,
    state,
    label,
    select: (next) => { if (orgId) select(orgId, next); },
    // Renders NOTHING when the org has no active branch — an org without
    // branches (every solo teacher, and every centre before its first) must see
    // zero new chrome. Unchanged binding rule from BranchPicker.
    //
    // THI-172: also nothing for a dormant (non-Center) org, even one that HAS
    // branches — the switcher itself is the one thing that goes fully dormant,
    // `!== false` fails OPEN while the subscription is still loading — ON
    // PURPOSE, and it must match useEffectiveBranchId's pending behaviour
    // exactly. The invariant is COHERENCE, not direction: chrome and filtering
    // resolve from the same predicate, so during the pending beat a dormant
    // org briefly shows the switcher WITH its old filter applied (honest, and
    // self-corrects in one round trip), never filtered data with no chrome to
    // explain it — which reads as data loss, the exact bug THI-172 exists to
    // kill. (A fail-closed switcher over a fail-open filter was the reviewed
    // v1 of this line; the review caught the disagreement window.)
    visible: !!orgId && branches.length > 0 && subscription?.plan.allowsBranches !== false,
  };
}
